import { useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import { ApiConstant } from "const";
import { TYPE_TO_OS, OS_OPTIONS, ITEMS_PER_PAGE, SelectOption } from "const/deviceManagement.const";
import { ApiUtils, HumpUtils, SystemUtils } from "utils";
import { mapApiGroupsToDisplay, DeviceGroup } from "pages/DeviceManagement/deviceManagement.helper";
import { DeviceTab, DEVICE_TAB_ACTIVE } from "const/deviceManagement.const";
import i18next from "i18next";

function buildDeviceTypeParam(
  selectedTypes: string[],
  selectedOS: string[]
): string | null {
  const osValues = new Set<number>();

  const hasTypeFilter = !selectedTypes.includes("all");
  const hasOSFilter = !selectedOS.includes("all");

  if (!hasTypeFilter && !hasOSFilter) return null;

  if (hasTypeFilter) {
    selectedTypes.forEach((t) => {
      (TYPE_TO_OS[t] || []).forEach((v: number) => osValues.add(v));
    });
  }

  if (hasOSFilter) {
    const osSet = new Set(selectedOS.map(Number));
    if (hasTypeFilter) {
      const intersection = new Set([...osValues].filter((v) => osSet.has(v)));
      osValues.clear();
      intersection.forEach((v) => osValues.add(v));
    } else {
      osSet.forEach((v) => osValues.add(v));
    }
  }

  if (osValues.size === 0) return "0";
  if (osValues.size === 5) return null;
  return Array.from(osValues).sort().join(",");
}

function computeFilteredOSOptions(selectedTypes: string[]): SelectOption[] {
  if (selectedTypes.includes("all")) return OS_OPTIONS;
  const allowed = new Set<number>();
  selectedTypes.forEach((t) => {
    (TYPE_TO_OS[t] || []).forEach((v: number) => allowed.add(v));
  });
  return OS_OPTIONS.filter((opt) => opt.value === "all" || allowed.has(opt.value as number));
}

interface ParsedParams {
  page: number;
  search: string;
  selectedTypes: string[];
  selectedOS: string[];
  selectedVersions: string[];
}

function parseSearchParams(searchParams: URLSearchParams): ParsedParams {
  const page = parseInt(searchParams.get("page") || "1") || 1;
  const search = searchParams.get("search") || "";
  const types = searchParams.get("types");
  const os = searchParams.get("os");
  const versions = searchParams.get("versions");

  return {
    page,
    search,
    selectedTypes: types ? types.split(",") : ["all"],
    selectedOS: os ? os.split(",") : ["all"],
    selectedVersions: versions ? versions.split(",") : ["all"],
  };
}

interface UseDeviceListOptions {
  activeTab?: DeviceTab;
}

export interface UseDeviceListReturn {
  loading: boolean;
  searchText: string;
  selectedTypes: string[];
  selectedOS: string[];
  selectedVersions: string[];
  filteredOSOptions: SelectOption[];
  versionOptions: SelectOption[];
  displayGroups: DeviceGroup[];
  page: number;
  totalPages: number;
  totalAccounts: number;
  isSelectAllMode: boolean;
  excludeDeviceIds: string[];
  totalDevices: number;
  deviceTypeParam: string | null;
  refetch: () => void;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleTypesChange: (newValues: string[]) => void;
  handleOSChange: (newValues: string[]) => void;
  handleVersionsChange: (newValues: string[]) => void;
  handleResetAll: () => void;
  handlePageChange: (event: React.ChangeEvent<unknown>, newPage: number) => void;
  handleToggleSelectAll: () => void;
  handleExcludeDevice: (deviceId: string) => void;
  setIsSelectAllMode: React.Dispatch<React.SetStateAction<boolean>>;
  setExcludeDeviceIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedDeviceIds: string[];
  setSelectedDeviceIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export default function useDeviceList(options: UseDeviceListOptions = {}): UseDeviceListReturn {
  const { activeTab = DEVICE_TAB_ACTIVE } = options;
  const history = useHistory();
  const location = useLocation();

  const initialParams = useMemo(
    () => parseSearchParams(new URLSearchParams(location.search)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [page, setPage] = useState(initialParams.page);
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);
  const [excludeDeviceIds, setExcludeDeviceIds] = useState<string[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [apiVersions, setApiVersions] = useState<string[]>([]);

  const [inputText, setInputText] = useState(initialParams.search);
  const [searchText, setSearchText] = useState(initialParams.search);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialParams.selectedTypes);
  const [selectedOS, setSelectedOS] = useState<string[]>(initialParams.selectedOS);
  const [selectedVersions, setSelectedVersions] = useState<string[]>(initialParams.selectedVersions);

  const deviceTypeParam = useMemo(
    () => buildDeviceTypeParam(selectedTypes, selectedOS),
    [selectedTypes, selectedOS]
  );

  const filteredOSOptions = useMemo(
    () => computeFilteredOSOptions(selectedTypes),
    [selectedTypes]
  );

  // Fetch available versions from server — auto-filters when deviceTypeParam changes
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const params: Record<string, string> = {};
        if (deviceTypeParam) {
          params.deviceType = deviceTypeParam;
        }
        const response = await ApiUtils.createApiWithToken(false).get(
          ApiConstant.DEVICES_VERSIONS,
          HumpUtils.decamelizeKeys(params)
        );
        if (response.status === ApiConstant.STT_OK) {
          const rawData = HumpUtils.camelizeKeys(response.data);
          const versions: string[] = rawData.data || rawData || [];
          setApiVersions(versions);
        }
      } catch {
        // ignore — version list is optional
      }
    };
    fetchVersions();
  }, [deviceTypeParam]);

  // Reset all filters & selections when switching tab
  useEffect(() => {
    setInputText("");
    setSearchText("");
    setSelectedTypes(["all"]);
    setSelectedOS(["all"]);
    setSelectedVersions(["all"]);
    setPage(1);
    setIsSelectAllMode(false);
    setExcludeDeviceIds([]);
    setSelectedDeviceIds([]);
  }, [activeTab]);

  // Sync state to URL query params
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", String(page));
    if (searchText) params.set("search", searchText);
    if (!selectedTypes.includes("all")) params.set("types", selectedTypes.join(","));
    if (!selectedOS.includes("all")) params.set("os", selectedOS.join(","));
    if (!selectedVersions.includes("all")) params.set("versions", selectedVersions.join(","));

    const newSearch = params.toString();
    const currentSearch = location.search.replace("?", "");

    if (newSearch !== currentSearch) {
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, selectedTypes, selectedOS, selectedVersions]);

  // Main device list query — auto-refetches when filters change
  const deviceListQuery = useQuery({
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: [ApiConstant.REACT_QUERY_KEYS.devicesFilter, activeTab, page, searchText, deviceTypeParam, selectedVersions],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        offset: (page - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        paging: 1,
        orderBy: "last_seen",
        asc: 0,
      };

      if (searchText && searchText.trim()) {
        params.search = searchText.trim();
      }

      if (deviceTypeParam) {
        params.deviceType = deviceTypeParam;
      }

      if (selectedVersions.length && !selectedVersions.includes("all")) {
        params.deviceVersion = selectedVersions[0];
      }

      if (activeTab === DEVICE_TAB_ACTIVE) {
        params.state = 1;
        params.blocked = 0;
      } else {
        params.state = 0;
        params.blocked = 1;
      }

      const response = await ApiUtils.createApiWithToken(false).get(
        ApiConstant.DEVICES_FILTER,
        HumpUtils.decamelizeKeys(params)
      );

      if (response.status === ApiConstant.STT_OK) {
        const rawData = response.data;
        const pageDto = HumpUtils.camelizeKeys(rawData.data);

        return {
          groups: mapApiGroupsToDisplay(pageDto.data),
          totalPages: pageDto.totalPages || 0,
          totalAccounts: pageDto.totalElements || 0,
          totalDevices: pageDto.totalDevices || 0,
        };
      }

      throw response;
    },
    onError: (error: unknown) => {
      console.error("Device list error:", error);
      SystemUtils.appendNotification(i18next.t("deviceManagement:TXT_DEVICE_FETCH_ERROR"), "error");
    },
  });

  // Format version options returned directly from backend API
  const versionOptions = useMemo<SelectOption[]>(() => {
    return [
      { value: "all", label: "Tất cả phiên bản" },
      ...apiVersions.map((v) => ({ value: v, label: v })),
    ];
  }, [apiVersions]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchText(value);
        setPage(1);
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputText(value);
    setIsSelectAllMode(false);
    setExcludeDeviceIds([]);
    setSelectedDeviceIds([]);
    debouncedSetSearch(value);
  };

  const handleTypesChange = (newValues: string[]) => {
    setSelectedTypes(newValues);
    setSelectedOS(["all"]);
    setPage(1);
    setIsSelectAllMode(false);
    setExcludeDeviceIds([]);
    setSelectedDeviceIds([]);
  };

  const handleOSChange = (newValues: string[]) => {
    setSelectedOS(newValues);
    setPage(1);
    setIsSelectAllMode(false);
    setExcludeDeviceIds([]);
    setSelectedDeviceIds([]);
  };

  const handleVersionsChange = (newValues: string[]) => {
    setSelectedVersions(newValues);
    setPage(1);
    setIsSelectAllMode(false);
    setExcludeDeviceIds([]);
    setSelectedDeviceIds([]);
  };

  const handleResetAll = () => {
    setInputText("");
    setSearchText("");
    setSelectedTypes(["all"]);
    setSelectedOS(["all"]);
    setSelectedVersions(["all"]);
    setPage(1);
    setIsSelectAllMode(false);
    setExcludeDeviceIds([]);
    setSelectedDeviceIds([]);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleToggleSelectAll = () => {
    if (isSelectAllMode) {
      setIsSelectAllMode(false);
      setExcludeDeviceIds([]);
      setSelectedDeviceIds([]);
    } else {
      setIsSelectAllMode(true);
      setExcludeDeviceIds([]);
      setSelectedDeviceIds([]);
    }
  };

  const handleExcludeDevice = (deviceId: string) => {
    setExcludeDeviceIds((prev) => {
      if (prev.includes(deviceId)) {
        return prev.filter((id) => id !== deviceId);
      }
      return [...prev, deviceId];
    });
  };

  const responseData = deviceListQuery.data;

  return {
    loading: deviceListQuery.isLoading,
    searchText: inputText,
    selectedTypes,
    selectedOS,
    selectedVersions,
    filteredOSOptions,
    versionOptions,
    displayGroups: responseData?.groups || [],
    page,
    totalPages: responseData?.totalPages || 0,
    totalAccounts: responseData?.totalAccounts || 0,
    isSelectAllMode,
    excludeDeviceIds,
    totalDevices: responseData?.totalDevices || 0,
    deviceTypeParam,
    refetch: deviceListQuery.refetch,
    handleSearchChange,
    handleTypesChange,
    handleOSChange,
    handleVersionsChange,
    handleResetAll,
    handlePageChange,
    handleToggleSelectAll,
    handleExcludeDevice,
    setIsSelectAllMode,
    setExcludeDeviceIds,
    selectedDeviceIds,
    setSelectedDeviceIds,
  };
}
